import {
  DataTablePagination,
  Typography,
  DataTable,
  Separator,
  Checkbox,
  Button,
  Form,
} from '@ui/components';
import { useCallback, useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Filters, Bell } from '@ui/components/icons';
import { HasPermission } from '@shared/components';
import { useNavigate } from 'react-router-dom';

import {
  NOTIFICATIONS_TABLE_FILTERS_DEFAULT_VALUES,
  useNotificationsTable,
} from './use-notifications-table';
import { NotificationsTableFilters } from '../notifications-table-filters/notifications-table-filters';
import { NotificationsBulkActions } from '../notifications-bulk-actions/notifications-bulk-actions';

export const NotificationsTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { columnsLength, filtersFrom, totalItems, isLoading, table, state } =
    useNotificationsTable();

  const selectedRows = useMemo(() => Object.keys(state.rowSelection), [state.rowSelection]);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const hasSelectedRows = selectedRows.length > 0;

  const handleClearSelection = useCallback(() => {
    table.setRowSelection({});
  }, [table]);

  const toggleFilters = useCallback(() => {
    setFiltersVisible((prev) => !prev);
  }, []);

  const handlePrevPage = useCallback(() => {
    table.previousPage();
  }, [table]);

  const handleNextPage = useCallback(() => {
    table.nextPage();
  }, [table]);

  const handleReset = useCallback(() => {
    filtersFrom.reset(NOTIFICATIONS_TABLE_FILTERS_DEFAULT_VALUES);
  }, [filtersFrom]);

  return (
    <div className="flex flex-col bg-table-background rounded-lg overflow-hidden">
      <div className="flex items-center px-4 py-3 gap-4">
        <Checkbox
          onClick={table.getToggleAllRowsSelectedHandler()}
          checked={table.getIsAllRowsSelected()}
        />

        <Typography variant="labelMediumMedium">
          {hasSelectedRows
            ? t('NOTIFICATIONS.TABLE_SELECTED_NOTIFICATIONS', {
                postProcess: 'interval',
                count: selectedRows.length,
              })
            : t('NOTIFICATIONS.TABLE_TOTAL_NOTIFICATIONS', { count: totalItems })}
        </Typography>

        <div className="ml-auto flex items-center gap-4">
          <Button onClick={toggleFilters} variant="ghost">
            <Filters />
            {t('NOTIFICATIONS.TABLE_FILTERS')}
          </Button>

          <DataTablePagination
            pageIndex={table.getState().pagination.pageIndex}
            pageSize={table.getState().pagination.pageSize}
            prevPageDisabled={!table.getCanPreviousPage()}
            nextPageDisabled={!table.getCanNextPage()}
            prevPage={handlePrevPage}
            nextPage={handleNextPage}
            totalRows={totalItems}
          />
        </div>
      </div>
      {filtersVisible && (
        <Form {...filtersFrom}>
          <NotificationsTableFilters
            controls={filtersFrom.control}
            className="px-4 pb-3 pt-1.5"
            reset={handleReset}
          />
        </Form>
      )}

      <HasPermission permissionFeature="NOTIFICATIONS_MANAGE_NOTIFICATIONS" permissionType="WRITE">
        {hasSelectedRows && (
          <NotificationsBulkActions
            onNotificationMarkAsRead={handleClearSelection}
            onNotificationRemove={handleClearSelection}
            selectedRows={selectedRows}
          />
        )}
      </HasPermission>

      <Separator />

      <DataTable
        noResultsComponent={
          <div className="flex flex-col items-center justify-center gap-6 p-12">
            <div className="size-20 bg-surface-hover rounded-full flex items-center justify-center">
              <Bell className="size-12 text-border-disabled" />
            </div>
            <Typography variant="labelMediumMedium" className="text-weak">
              <Trans i18nKey="NOTIFICATIONS.TABLE_NO_RESULTS" />
            </Typography>

            <Button onClick={() => navigate('..')}>
              {t('NOTIFICATIONS.TABLE_NO_RESULTS_RETURN_TO_DASHBOARD')}
            </Button>
          </div>
        }
        className="h-full overflow-auto"
        columnsLength={columnsLength}
        isLoading={isLoading}
        table={table}
        hideHeader
      />
    </div>
  );
};
